import type { components } from '@/generated/bff';

export type BffFieldError = components['schemas']['FieldError'];

/**
 * The Portal app's OWN, narrow problem type — deliberately NOT a reuse of
 * the CRM's `@/shared/api/problem.ts` `Problem`/`QflyError`, even though both
 * describe the same RFC 7807 wire shape.
 *
 * Found at swarm review (typescript-critic + api-contract-consumer-critic,
 * independently convergent): `qfly_shared.errors.handlers`'s real runtime
 * handler always emits a `code` field (a machine-readable discriminator —
 * e.g. `session_expired`, `invalid_code`, `not_eligible` — this Portal's own
 * 401 handling depends on), but neither the CRM's hand-written `Problem`
 * interface NOR the generated `components['schemas']['Problem']` (itself
 * generated from `docs/contracts/shared/error-model.yaml`, confirmed by
 * reading `frontend/src/generated/bff.ts` directly) declares it — a
 * pre-existing, repo-wide contract gap this Portal app works around locally
 * rather than silently inheriting. A second reason to keep this separate
 * from the CRM's `Problem`/`toProblem()`: that parser carries CRM-specific
 * special-case handling (e.g. the non-7807 `stage_transition_blocked` shape)
 * that has nothing to do with this Portal and shouldn't be dragged into a
 * second, unrelated frontend entry point's error handling.
 */
export interface PortalProblem {
  readonly status: number;
  readonly title: string;
  readonly detail?: string;
  /** Machine-readable discriminator — e.g. "session_expired", "invalid_code",
   * "not_eligible". Absent on a generic/unclassified error. */
  readonly code?: string;
  readonly fieldErrors: ReadonlyArray<BffFieldError>;
}

/** Typed error thrown by the Portal's axios response interceptor for every
 * non-2xx response. Never thrown for a network/transport failure (those
 * surface as the original AxiosError — see `portalApiClient.ts`). */
export class PortalApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly detail?: string;
  public readonly fieldErrors: ReadonlyArray<BffFieldError>;

  constructor(problem: PortalProblem) {
    super(problem.detail ?? problem.title ?? `Request failed (${problem.status})`);
    this.name = 'PortalApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.detail = problem.detail;
    this.fieldErrors = problem.fieldErrors;
  }
}

export const isPortalApiError = (value: unknown): value is PortalApiError =>
  value instanceof PortalApiError;

/** Defensive parse, mirroring `@/shared/api/problem.ts::toProblem`'s own
 * "never trust the shape, always fall back" discipline — a malformed/
 * non-JSON body (e.g. a bare ASGI-layer error) must never throw a SECOND,
 * unrelated exception out of the response interceptor itself. */
export const toPortalProblem = (raw: unknown, fallbackStatus: number): PortalProblem => {
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const status = typeof obj.status === 'number' ? obj.status : fallbackStatus;
    const title = typeof obj.title === 'string' ? obj.title : 'Unexpected error';
    const detail = typeof obj.detail === 'string' ? obj.detail : undefined;
    const code = typeof obj.code === 'string' ? obj.code : undefined;
    const fieldErrors = Array.isArray(obj.errors)
      ? obj.errors.filter(
          (e): e is BffFieldError =>
            !!e && typeof e === 'object' && typeof (e as { message?: unknown }).message === 'string',
        )
      : [];
    return { status, title, detail, code, fieldErrors };
  }
  return { status: fallbackStatus, title: 'Unexpected error', fieldErrors: [] };
};
